import type { TaskDocument } from '@/lib/db/rxdb/schemas/task'

type TaskDocumentWithIdentifierFields = TaskDocument & {
  taskIdentifier?: string | null
  identifier?: string | null
  displayId?: string | null
  taskKey?: string | null
  key?: string | null
  issueKey?: string | null
  referenceCode?: string | null
  externalReferenceId?: string | null
  shortId?: string | null
  seqNo?: number | null
  sequenceNumber?: number | null
  seq_no?: number | null
}

const FRIENDLY_IDENTIFIER_REGEX = /^[A-Z]+-\d+$/i

/** The KEY part of a `KEY-123` identifier, or null when it isn't that shape. */
const identifierPrefix = (identifier: string): string | null => {
  const separator = identifier.lastIndexOf('-')
  if (separator <= 0) {
    return null
  }
  return identifier.slice(0, separator)
}

export const resolveTaskIdentifier = (
  task: TaskDocument | null | undefined,
  projectKey?: string | null,
): string => {
  if (!task) {
    return ''
  }

  const extended = task as TaskDocumentWithIdentifierFields
  const identifierCandidate = [
    extended.taskIdentifier,
    extended.identifier,
    extended.taskKey,
    extended.issueKey,
    extended.key,
    extended.displayId,
    extended.referenceCode,
    extended.externalReferenceId,
    extended.shortId,
  ].find((value) => typeof value === 'string' && value.trim().length > 0)

  const seqValue =
    extended.seqNo ?? extended.sequenceNumber ?? extended.seq_no ?? null

  if (identifierCandidate) {
    const stored = identifierCandidate.trim()
    const storedPrefix = identifierPrefix(stored)
    const prefixMismatch =
      !!projectKey &&
      projectKey.length > 0 &&
      storedPrefix !== null &&
      storedPrefix.toLowerCase() !== projectKey.toLowerCase()

    if (!prefixMismatch) {
      return stored
    }

    // Stored identifier belongs to another project (e.g. parent space after a
    // move, or stale RxDB cache). The old seqNo is also per-project, so do not
    // recompose KEY-oldSeq — that would show MOL-1 when the server has MOL-30.
    // Show nothing until pull delivers the leaf project's identifier.
    return ''
  }

  if (
    seqValue !== null &&
    projectKey &&
    projectKey.length > 0
  ) {
    return `${projectKey}-${seqValue}`
  }

  if (FRIENDLY_IDENTIFIER_REGEX.test(task.id)) {
    return task.id
  }

  return ''
}
